/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Search,
  Filter,
  AlertCircle,
  UserPlus,
  ChevronDown,
  Loader2,
  Info,
  AlertTriangle,
  Briefcase,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";

// Confidence level styles
const confidenceStyles = {
  Excellent:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  Good: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  Fair: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  Poor: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const calculateMatchScoreFromBreakdown = (matchBreakdown, fallbackScore = 0) => {
  const fallback = Number(fallbackScore);

  if (!matchBreakdown || typeof matchBreakdown !== "object") {
    return Number.isFinite(fallback) ? fallback : 0;
  }

  const totals = Object.values(matchBreakdown).reduce(
    (acc, value) => {
      if (!value || typeof value !== "object") return acc;

      const score = Number(value.score);
      const max = Number(value.max);

      if (!Number.isFinite(score) || !Number.isFinite(max) || max <= 0) {
        return acc;
      }

      return {
        score: acc.score + score,
        max: acc.max + max,
      };
    },
    { score: 0, max: 0 }
  );

  if (totals.max <= 0) {
    return Number.isFinite(fallback) ? fallback : 0;
  }

  return Math.min(100, Math.max(0, (totals.score / totals.max) * 100));
};

// Derive confidence label from the displayed percentage score
const getConfidenceLabel = (percentScore) => {
  if (percentScore >= 85) return "Excellent";
  if (percentScore >= 60) return "Good";
  if (percentScore >= 40) return "Fair";
  return "Poor";
};

const AssignCaregiver = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    matchCareGiversToJob,
    matchedCareGivers,
    assignCaregiverToJob,
    jobs,
  } = useContext(AdminContext);

  // Data State
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [excludeEmployed, setExcludeEmployed] = useState(true);
  const [subcityFilter, setSubcityFilter] = useState("All");

  // Selection & Details
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [assignmentError, setAssignmentError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Get job details for display
  const currentJob = jobs?.find((job) => job.id === jobId);

  // Fetch data with error handling
  useEffect(() => {
    const fetchData = async () => {
      if (!jobId) {
        toast.error("No job ID provided");
        navigate("/requests/manage");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Just match caregivers
        await matchCareGiversToJob(jobId);
      } catch (error) {
        console.error("Error in fetchData:", error);
        setError(error.message || "Failed to load caregiver matches");
        toast.error("Failed to load caregiver matches");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobId, navigate]);

  // Map data with new fields (confidence + matchBreakdown)
  useEffect(() => {
    try {
      if (!matchedCareGivers || !Array.isArray(matchedCareGivers)) {
        setCaregivers([]);
        return;
      }

      const mapped = matchedCareGivers
        .map((caregiverData) => {
          const { caregiver, matchScore, confidence, matchBreakdown } =
            caregiverData || {};

          if (!caregiver) {
            console.warn("Invalid caregiver data:", caregiverData);
            return null;
          }

          const calculatedMatchScore = calculateMatchScoreFromBreakdown(
            matchBreakdown,
            matchScore
          );

          return {
            id: caregiver.id,
            name: caregiver.name || "Unknown Caregiver",
            avatar: caregiver.image || "/default-avatar.png",
            phone: caregiver.phoneNumber || "N/A",
            category:
              caregiver.serviceCategory?.label || caregiver.category || "N/A",
            subcity: caregiver.subcity || "N/A",
            experience: `${caregiver.experience || 0} Years`,
            matchScore: calculatedMatchScore,
            confidence: getConfidenceLabel(calculatedMatchScore),
            matchBreakdown: matchBreakdown || {},
            jobStatus:
              caregiver.currentAppointment?.status === "Work Started"
                ? "Work Started"
                : "Open to Work",
            // Check for onboarding status if available in backend response
            onboardingStatus: caregiver.onboardingStatus || null, // 'pending', 'completed', 'incomplete'
            hasCurrentJob: !!caregiver.currentAppointment,
            currentJobInfo: caregiver.currentAppointment || null,
            // Dynamic field values
            fieldValues: caregiver.fieldValues || [],
            serviceCategory: caregiver.serviceCategory || null,
          };
        })
        .filter(Boolean);

      setCaregivers(mapped);
    } catch (error) {
      console.error("Error mapping caregivers:", error);
      setCaregivers([]);
    }
  }, [matchedCareGivers]);

  // Filter Logic (client-side)
  const filteredCaregivers = caregivers
    .filter((c) => {
      const matchesSearch = c.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesEmployed = excludeEmployed
        ? c.jobStatus !== "Work Started"
        : true;
      const matchesSubcity =
        subcityFilter === "All" || c.subcity === subcityFilter;

      return matchesSearch && matchesEmployed && matchesSubcity;
    })
    .sort((a, b) => b.matchScore - a.matchScore);

  // Calculate pagination
  const totalItems = filteredCaregivers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCaregivers = filteredCaregivers.slice(startIndex, endIndex);

  // Reset pagination on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, excludeEmployed, subcityFilter]);

  const handleConfirmAssignment = async () => {
    if (!selectedCaregiver) return;

    setLoading(true);
    setAssignmentError(null);

    const result = await assignCaregiverToJob(jobId, selectedCaregiver.id);

    if (result.success) {
      navigate("/requests/manage");
    } else {
      // Display specific error
      setAssignmentError({
        type: result.type,
        message: result.message,
      });
    }

    setLoading(false);
  };

  // Handle retry
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    if (jobId) {
      matchCareGiversToJob(jobId)
        .catch((error) => {
          setError(error.message || "Failed to load caregivers");
          toast.error("Failed to load caregiver matches");
        })
        .finally(() => setLoading(false));
    }
  };

  // Open details modal
  const openDetailsModal = (caregiver) => {
    setSelectedDetails(caregiver);
    setShowDetailsModal(true);
  };

  // Helper to format breakdown key
  const formatBreakdownKey = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/requests/manage")}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-text dark:text-dark-text"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Assign Caregiver
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            {currentJob ? (
              <>
                Matching for:{" "}
                <span className="font-semibold text-primary">
                  &quot;{currentJob.description?.substring(0, 50)}&quot;
                </span>
              </>
            ) : (
              <>
                Matching Algorithm for Request{" "}
                <span className="font-semibold text-primary">
                  #{jobId?.substring(0, 8)}
                </span>
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[11px] text-gray-400 dark:text-dark-muted font-medium">Confidence:</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">≥85% Excellent</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">≥60% Good</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">≥40% Fair</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{"<"}40% Poor</span>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle
              className="text-red-500 flex-shrink-0 mt-0.5"
              size={20}
            />
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-300 font-medium">
                Failed to load caregivers
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                {error}
              </p>
              <button
                onClick={handleRetry}
                className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mb-6 flex items-center justify-center gap-3 p-4 bg-card dark:bg-dark-surface rounded-2xl">
          <Loader2 className="animate-spin text-primary" size={24} />
          <p className="text-text dark:text-dark-text font-medium">
            Loading caregiver matches...
          </p>
        </div>
      )}

      {/* Modern Filter Section */}
      {!loading && !error && caregivers.length > 0 && (
        <div className="bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border mb-6">
          {/* Top Row */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-primary"
                size={20}
              />
              <input
                type="text"
                placeholder="Search caregiver by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted font-medium transition-all"
                disabled={loading}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all ${
                  showFilters
                    ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary ring-2 ring-primary/20"
                    : "bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800"
                } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Filter size={18} />
                <span>{showFilters ? "Hide Filters" : "More Filters"}</span>
              </button>
            </div>
          </div>

          {/* Collapsible Section */}
          <div
            className={`grid grid-cols-1 md:grid-cols-1 gap-4 overflow-hidden transition-all duration-300 ease-in-out ${
              showFilters
                ? "max-h-40 opacity-100 mt-4 pt-4 border-t border-gray-100 dark:border-dark-border"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
                Subcity
              </label>
              <div className="relative">
                <select
                  value={subcityFilter}
                  onChange={(e) => setSubcityFilter(e.target.value)}
                  disabled={loading}
                  className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-primary/20 outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="All">All Subcities</option>
                  <option value="Addis Ketema">Addis Ketema</option>
                  <option value="Akaki Kaliti">Akaki Kaliti</option>
                  <option value="Arada">Arada</option>
                  <option value="Bole">Bole</option>
                  <option value="Gullele">Gullele</option>
                  <option value="Kirkos">Kirkos</option>
                  <option value="Kolfe Keranio">Kolfe Keranio</option>
                  <option value="Lideta">Lideta</option>
                  <option value="Nifas Silk-Lafto">Nifas Silk-Lafto</option>
                  <option value="Yeka">Yeka</option>
                  <option value="Lemi Kura">Lemi Kura</option>
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-primary pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && caregivers.length === 0 && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <UserPlus className="text-primary" size={28} />
          </div>
          <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
            No Caregivers Found
          </h3>
          <p className="text-gray-500 dark:text-dark-muted max-w-md mx-auto mb-6">
            {matchedCareGivers && matchedCareGivers.length === 0
              ? "No caregivers match the requirements for this job. Try adjusting the job requirements or check back later."
              : "Unable to load caregiver data. Please try again."}
          </p>
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors"
          >
            Refresh Matches
          </button>
        </div>
      )}

      {/* Match Table */}
      {!loading && !error && caregivers.length > 0 && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 font-bold">Match Score</th>
                  <th className="px-6 py-4 font-bold">Caregiver</th>
                  <th className="px-6 py-4 font-bold">Confidence</th>
                  <th className="px-6 py-4 font-bold">Category</th>
                  <th className="px-6 py-4 font-bold">Experience</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginatedCaregivers.map((cg) => (
                  <tr
                    key={cg.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      selectedCaregiver?.id === cg.id
                        ? "bg-primary/5 dark:bg-primary/10"
                        : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="relative w-10 h-10 flex items-center justify-center">
                          <svg
                            className="w-full h-full transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <circle
                              className="text-gray-200 dark:text-gray-700"
                              cx="18"
                              cy="18"
                              r="15.9155"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                            />
                            <circle
                              className="text-primary transition-all duration-500 ease-out"
                              cx="18"
                              cy="18"
                              r="15.9155"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeDasharray="100"
                              strokeDashoffset={100 - cg.matchScore}
                            />
                          </svg>
                          <span className="absolute text-[10px] font-bold text-text dark:text-dark-text">
                            {cg.matchScore.toFixed(1)}%
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-dark-muted font-medium">
                          Compatible
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={cg.avatar}
                            className="w-10 h-10 rounded-full object-cover bg-gray-100 dark:bg-gray-700"
                            alt={cg.name}
                          />
                          {/* Onboarding Status Indicator */}
                          {cg.onboardingStatus &&
                            cg.onboardingStatus !== "completed" && (
                              <div
                                className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white dark:border-dark-surface flex items-center justify-center"
                                title="Onboarding Pending"
                              >
                                <AlertTriangle
                                  size={8}
                                  className="text-white"
                                />
                              </div>
                            )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-text dark:text-dark-text">
                              {cg.name}
                            </p>
                            {/* Current Job Indicator */}
                            {cg.hasCurrentJob && (
                              <Briefcase
                                size={12}
                                className="text-red-500"
                                title="Has current job"
                              />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-dark-muted">
                            {cg.phone} • {cg.subcity}
                          </p>
                          {cg.onboardingStatus &&
                            cg.onboardingStatus !== "completed" && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-0.5">
                                Onboarding {cg.onboardingStatus}
                              </p>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          confidenceStyles[cg.confidence]
                        }`}
                      >
                        {cg.confidence}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700 dark:text-dark-muted">
                      {cg.category}
                    </td>
                    <td className="px-6 py-4 text-gray-700 dark:text-dark-muted">
                      {cg.experience}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            cg.jobStatus === "Work Started"
                              ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                          }`}
                        >
                          {cg.jobStatus}
                        </span>
                        {/* Visual indicator for current job conflict */}
                        {cg.hasCurrentJob && (
                          <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">
                            Currently assigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openDetailsModal(cg)}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-text hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                        >
                          <Info size={12} /> Details
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCaregiver(cg);
                            setShowConfirmModal(true);
                          }}
                          className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-xs font-bold shadow-sm shadow-primary/20 dark:shadow-none transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredCaregivers.length > itemsPerPage && (
            <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                totalItems={totalItems}
              />
            </div>
          )}
        </div>
      )}

      {/* Match Details Modal */}
      {showDetailsModal && selectedDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-dark-border flex justify-between items-center shrink-0">
              <h2 className="text-xl font-bold text-text dark:text-dark-text">
                Match Breakdown
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <img
                    src={selectedDetails.avatar}
                    className="w-16 h-16 rounded-full object-cover bg-gray-100 dark:bg-gray-700"
                    alt={selectedDetails.name}
                  />
                  {selectedDetails.onboardingStatus &&
                    selectedDetails.onboardingStatus !== "completed" && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full border-2 border-white dark:border-dark-surface flex items-center justify-center">
                        <AlertTriangle size={10} className="text-white" />
                      </div>
                    )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text dark:text-dark-text">
                    {selectedDetails.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-bold text-primary">
                      {selectedDetails.matchScore.toFixed(1)}%
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        confidenceStyles[selectedDetails.confidence]
                      }`}
                    >
                      {selectedDetails.confidence}
                    </span>
                  </div>
                  {selectedDetails.hasCurrentJob && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 dark:text-red-400 text-xs font-medium">
                      <Briefcase size={12} />
                      <span>Currently has job assignment</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 mb-6 border border-gray-100 dark:border-dark-border">
                <h4 className="text-sm font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-4">
                  Match Criteria Score
                </h4>
                <div className="space-y-4">
                  {Object.entries(selectedDetails.matchBreakdown).map(
                    ([key, value]) => {
                      if (!value) return null;

                      const isMatched =
                        value.matched !== undefined
                          ? value.matched
                          : value.score > 0;
                      const scoreFormatted = Number(value.score).toFixed(1);
                      const maxFormatted = Number(value.max).toFixed(1);

                      return (
                        <div key={key} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-text dark:text-dark-text">
                                {formatBreakdownKey(key)}
                              </span>

                              {/* Extra contextual details for premium look */}
                              {key === "experience" && value.required && (
                                <span className="text-[10px] text-gray-500 dark:text-dark-muted mt-0.5">
                                  Required:{" "}
                                  {value.required[0] === 0 &&
                                  value.required[1] === 99
                                    ? "Any"
                                    : `${value.required[0]}-${value.required[1]} years`}{" "}
                                  • Candidate: {value.caregiverYears} years
                                </span>
                              )}
                              {key === "rating" &&
                                value.caregiverRating !== undefined && (
                                  <span className="text-[10px] text-gray-500 dark:text-dark-muted mt-0.5">
                                    Rating: {value.caregiverRating.toFixed(1)} /
                                    5.0
                                  </span>
                                )}
                              {key === "languages" && (
                                <span className="text-[10px] text-gray-500 dark:text-dark-muted mt-0.5">
                                  Weighted by matched languages
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2.5">
                              <span className="text-xs font-mono font-medium text-gray-500 dark:text-dark-muted bg-gray-200/40 dark:bg-gray-700/50 px-2 py-0.5 rounded-lg border border-gray-100 dark:border-dark-border">
                                {scoreFormatted}/{maxFormatted} pts
                              </span>
                              <span
                                className={`px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide ${
                                  isMatched
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/30"
                                    : "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 border border-red-200/30"
                                }`}
                              >
                                {isMatched ? "Matched" : "Mismatched"}
                              </span>
                            </div>
                          </div>

                          {/* Render sub-details for dynamic fields if they exist */}
                          {key === "fields" &&
                            value.detail &&
                            Object.keys(value.detail).length > 0 && (
                              <div className="pl-4 border-l border-gray-200 dark:border-gray-700 mt-2 space-y-2 py-1">
                                {Object.entries(value.detail).map(
                                  ([fieldKey, fieldVal]) => {
                                    const fieldMatched =
                                      fieldVal.matched !== undefined
                                        ? fieldVal.matched
                                        : fieldVal.score > 0;
                                    return (
                                      <div
                                        key={fieldKey}
                                        className="flex items-center justify-between text-xs"
                                      >
                                        <span className="text-gray-500 dark:text-dark-muted font-medium">
                                          {formatBreakdownKey(fieldKey)}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-mono text-gray-400 dark:text-dark-muted bg-gray-100 dark:bg-gray-800 px-1 py-0.2 rounded">
                                            {Number(fieldVal.score).toFixed(1)}
                                          </span>
                                          <span
                                            className={`text-[10px] font-bold ${fieldMatched ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}
                                          >
                                            {fieldMatched
                                              ? "Matched"
                                              : "Not Matched"}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                  <span className="text-gray-500 dark:text-dark-muted text-xs">
                    Category
                  </span>
                  <p className="font-medium text-text dark:text-dark-text mt-1">
                    {selectedDetails.category}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                  <span className="text-gray-500 dark:text-dark-muted text-xs">
                    Experience
                  </span>
                  <p className="font-medium text-text dark:text-dark-text mt-1">
                    {selectedDetails.experience}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                  <span className="text-gray-500 dark:text-dark-muted text-xs">
                    Location
                  </span>
                  <p className="font-medium text-text dark:text-dark-text mt-1">
                    {selectedDetails.subcity}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg">
                  <span className="text-gray-500 dark:text-dark-muted text-xs">
                    Status
                  </span>
                  <p
                    className={`font-medium mt-1 ${selectedDetails.jobStatus === "Work Started" ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}
                  >
                    {selectedDetails.jobStatus}
                  </p>
                </div>
              </div>

              {/* Dynamic Field Values */}
              {selectedDetails.fieldValues &&
                selectedDetails.fieldValues.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase mb-3">
                      {selectedDetails.serviceCategory?.label || "Category"}{" "}
                      Specific Details
                    </h4>
                    <div className="space-y-2">
                      {selectedDetails.fieldValues.map((fv) => (
                        <div
                          key={fv.id}
                          className="p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg"
                        >
                          <span className="text-gray-500 dark:text-dark-muted text-xs block mb-1">
                            {fv.field?.label || "Field"}
                          </span>
                          <div className="font-medium text-text dark:text-dark-text text-sm">
                            {Array.isArray(fv.values) ? (
                              fv.values.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {fv.values.map((val, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded text-xs"
                                    >
                                      {val}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 dark:text-dark-muted">
                                  Not specified
                                </span>
                              )
                            ) : (
                              <span>{fv.values || "Not specified"}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedCaregiver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-4 p-1 relative">
              <img
                src={selectedCaregiver.avatar}
                className="w-full h-full rounded-full object-cover"
                alt={selectedCaregiver.name}
              />
              {selectedCaregiver.onboardingStatus &&
                selectedCaregiver.onboardingStatus !== "completed" && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full border-2 border-white dark:border-dark-surface flex items-center justify-center">
                    <AlertTriangle size={12} className="text-white" />
                  </div>
                )}
            </div>
            <h3 className="text-xl font-bold mb-1 text-text dark:text-dark-text">
              Assign {selectedCaregiver.name}?
            </h3>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-primary font-bold">
                {selectedCaregiver.matchScore.toFixed(1)}%
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  confidenceStyles[selectedCaregiver.confidence]
                }`}
              >
                {selectedCaregiver.confidence}
              </span>
            </div>

            {/* Error Display */}
            {assignmentError && (
              <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                    size={20}
                  />
                  <div>
                    <h4 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                      {assignmentError.type === "onboarding-required" &&
                        "Onboarding Required"}
                      {assignmentError.type === "caregiver-busy" &&
                        "Caregiver Unavailable"}
                      {assignmentError.type === "category-mismatch" &&
                        "Category Mismatch"}
                      {assignmentError.type === "client-conflict" &&
                        "Client Conflict"}
                      {assignmentError.type === "general" &&
                        "Assignment Failed"}
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {assignmentError.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for busy/onboarding caregivers */}
            {(selectedCaregiver.hasCurrentJob ||
              (selectedCaregiver.onboardingStatus &&
                selectedCaregiver.onboardingStatus !== "completed")) &&
              !assignmentError && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                      size={16}
                    />
                    <div className="text-left">
                      {selectedCaregiver.hasCurrentJob && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          This caregiver currently has an active job assignment.
                        </p>
                      )}
                      {selectedCaregiver.onboardingStatus &&
                        selectedCaregiver.onboardingStatus !== "completed" && (
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            Onboarding is not completed for this caregiver.
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              )}

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-left mb-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-dark-muted">
                  Experience
                </span>
                <span className="font-medium text-text dark:text-dark-text">
                  {selectedCaregiver.experience}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-dark-muted">
                  Location
                </span>
                <span className="font-medium text-text dark:text-dark-text">
                  {selectedCaregiver.subcity}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-dark-muted">
                  Category
                </span>
                <span className="font-medium text-text dark:text-dark-text">
                  {selectedCaregiver.category}
                </span>
              </div>
              {selectedCaregiver.hasCurrentJob && (
                <div className="flex justify-between text-red-600 dark:text-red-400">
                  <span className="text-xs">Current Status</span>
                  <span className="font-medium text-xs">Assigned to job</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-dark-text rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAssignment}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Assigning...
                  </>
                ) : (
                  "Confirm Assignment"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignCaregiver;
